import { memo, useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useField } from "formik";
import { v4 as uuidv4 } from "uuid";
import { Button, Card, Flex, Select, Text } from "@jsq/ds/core";
import { useFormState } from "@jsq/ds/forms";
import {
  findAttributeByPath,
  toAttributeLeaves,
  type AttributeFilter,
  type AttributeLeaf,
} from "./attributeCatalogue";
import { ConjunctionDivider } from "./ConjunctionDivider";
import {
  DEFAULT_CONDITION_OPERATOR,
  OPERATOR_OPTIONS,
  operatorsForDataType,
} from "./constants";
import { RuleValueEditor } from "./RuleValueEditor";
import { SelectAttributeModal } from "./SelectAttributeModal";
import * as styles from "./styles_vanilla.css";
import {
  isSetOperator,
  type FilterOperator,
  type RuleClause,
  type RuleClauseValue,
  type RuleGroup,
  type Rules,
} from "./types";
import { useAllPrimaryFields } from "./useAllPrimaryFields";
import { candidateTokensForAttribute } from "./utils";

type RulesSectionProps = {
  /** Card title (e.g. "Filter rules"). */
  title: string;
  /** Card subtitle / one-line description below the title. */
  description: string;
  /** Formik field name pointing at the `Rules` object. */
  name: string;
  /** When true, all controls are disabled, the trash + add buttons are
   * hidden, and the attribute pill no longer opens the picker modal. */
  isReadOnly?: boolean;
  /** Restrict the attribute picker to a subset of the catalogue (e.g. by
   * `dataType` for field-level consumers). */
  attributeFilter?: AttributeFilter;
  /** Arena context used to fetch possible values for the selected attribute. */
  arenaId?: number | null;
  /** Opt in to the "Values selected at runtime" operator. Off by default:
   * a runtime condition binds the whole rule owner to the recipient's
   * selection, which is only meaningful per field (or per formula
   * component). Query- and footnote-level owners must stay literal-only. */
  allowRuntimeValues?: boolean;
  /** Test/override hook. Omit in production to use `finpubPrimaryFields`. */
  attributeLeaves?: ReadonlyArray<AttributeLeaf>;
  /** When `"inline"`, renders the title/description as plain text instead
   * of an outer Card (for embedding inside an accordion panel). */
  variant?: "card" | "inline";
};

type ModalState =
  | { mode: "closed" }
  | { mode: "addGroup" }
  | { mode: "addClause"; groupId: string }
  | { mode: "editClause"; groupId: string; clauseId: string };

const RUNTIME_OPERATOR_VALUE = "runtime_input";
const RUNTIME_OPERATOR_LABEL = "Values selected at runtime";
const GL_TRANSACTION_IDS_TOKEN = "gl_transaction_ids";

const newClause = (attributePath: string): RuleClause => ({
  id: `rule_${uuidv4()}`,
  attribute: attributePath,
  operator: "eq",
  value: null,
  valueKind: "LITERAL",
});

const newGroup = (attributePath: string): RuleGroup => ({
  id: `rule_${uuidv4()}`,
  conditionOperator: DEFAULT_CONDITION_OPERATOR,
  clauses: [newClause(attributePath)],
});

const isFilterOperator = (candidate: unknown): candidate is FilterOperator =>
  OPERATOR_OPTIONS.some((option) => option.value === candidate);

/**
 * Swap a clause's attribute, resetting any value that's no longer guaranteed
 * to be valid. Re-confirming the same attribute is treated as a no-op so the
 * user doesn't lose their selection if they accidentally open the picker and
 * click "Update". When the attribute actually changes, we reset to the same
 * defaults `newClause` uses (the value catalogue — and, in the future, the
 * operator catalogue — belongs to the attribute, so leaving the previous
 * value in place could leave the form holding stale/invalid data).
 */
const replaceClauseAttribute = (clause: RuleClause, attributePath: string): RuleClause => {
  if (clause.attribute === attributePath) return clause;
  return {
    ...clause,
    attribute: attributePath,
    operator: "eq",
    value: null,
    valueKind: "LITERAL",
  };
};

/**
 * Operator changes that cross the scalar↔set boundary (e.g. `eq` → `in`)
 * make the existing `value` shape invalid; reset to `null` in that case.
 * Same-shape changes (e.g. `eq` → `gt`) leave the value alone — the user's
 * input is still meaningful even if the comparator changed.
 */
const updateClauseOperator = (clause: RuleClause, next: FilterOperator): RuleClause => {
  if (clause.operator === next) return clause;
  const wasSet = isSetOperator(clause.operator);
  const isSet = isSetOperator(next);
  if (wasSet !== isSet) return { ...clause, operator: next, value: null };
  return { ...clause, operator: next };
};

/**
 * Pure reducer for the "user just confirmed an attribute in the picker"
 * action, branching on which entry-point opened the picker (add a new group,
 * add a clause to an existing group, or edit an existing clause's
 * attribute). Pulled out of the component so the branches read top-to-bottom
 * instead of being buried inside an IIFE inside a `useCallback`.
 */
const applyAttributeToRules = (
  rules: Rules,
  modalState: ModalState,
  attributePath: string,
): RuleGroup[] => {
  switch (modalState.mode) {
    case "addGroup":
      return [...rules.groups, newGroup(attributePath)];
    case "addClause":
      return rules.groups.map((group) =>
        group.id === modalState.groupId
          ? { ...group, clauses: [...group.clauses, newClause(attributePath)] }
          : group,
      );
    case "editClause":
      return rules.groups.map((group) =>
        group.id === modalState.groupId
          ? {
              ...group,
              clauses: group.clauses.map((clause) =>
                clause.id === modalState.clauseId
                  ? replaceClauseAttribute(clause, attributePath)
                  : clause,
              ),
            }
          : group,
      );
    default:
      return rules.groups;
  }
};

type RuleClauseRowProps = {
  clause: RuleClause;
  groupId: string;
  /** Whether the form has been submitted (drives error rendering). */
  showError: boolean;
  isReadOnly: boolean;
  arenaId?: number | null;
  allowRuntimeValues: boolean;
  attributeLeaves: ReadonlyArray<AttributeLeaf>;
  onChange: (groupId: string, next: RuleClause) => void;
  onRemove: (groupId: string, clauseId: string) => void;
  onEditAttribute: (groupId: string, clauseId: string) => void;
};

const RuleClauseRow = memo(
  ({
    clause,
    groupId,
    showError,
    isReadOnly,
    arenaId,
    allowRuntimeValues,
    attributeLeaves,
    onChange,
    onRemove,
    onEditAttribute,
  }: RuleClauseRowProps) => {
    const attribute = clause.attribute
      ? findAttributeByPath(clause.attribute, attributeLeaves)
      : undefined;
    const operatorOptions = useMemo(
      () => operatorsForDataType(attribute?.dataType),
      [attribute?.dataType],
    );
    const usesRuntimeValues = clause.valueKind === "RUNTIME_INPUT";
    const supportsRuntimeValues =
      allowRuntimeValues &&
      candidateTokensForAttribute(attribute?.path).includes(GL_TRANSACTION_IDS_TOKEN);
    const displayedOperatorOptions = useMemo(
      () => [
        ...operatorOptions,
        // A clause already stored as RUNTIME_INPUT keeps the option even
        // where authoring one is disallowed, so the Select still has a
        // matching entry to render instead of falling back to blank.
        ...(supportsRuntimeValues || usesRuntimeValues
          ? [{ value: RUNTIME_OPERATOR_VALUE, label: RUNTIME_OPERATOR_LABEL }]
          : []),
      ],
      [operatorOptions, supportsRuntimeValues, usesRuntimeValues],
    );
    const pillLabel = attribute?.pillLabel ?? "Choose attribute";

    const handleOperatorChange = useCallback(
      (next: unknown) => {
        if (next === RUNTIME_OPERATOR_VALUE && supportsRuntimeValues) {
          onChange(groupId, {
            ...clause,
            operator: "in",
            value: GL_TRANSACTION_IDS_TOKEN,
            valueKind: "RUNTIME_INPUT",
          });
          return;
        }
        if (!isFilterOperator(next)) return;

        const literalClause: RuleClause = usesRuntimeValues
          ? { ...clause, value: null, valueKind: "LITERAL" }
          : clause;
        onChange(groupId, updateClauseOperator(literalClause, next));
      },
      [clause, groupId, onChange, supportsRuntimeValues, usesRuntimeValues],
    );

    const handleValueChange = useCallback(
      (next: RuleClauseValue) => onChange(groupId, { ...clause, value: next }),
      [clause, groupId, onChange],
    );

    const handleRemove = useCallback(
      () => onRemove(groupId, clause.id),
      [clause.id, groupId, onRemove],
    );

    const handleEditAttribute = useCallback(
      () => onEditAttribute(groupId, clause.id),
      [clause.id, groupId, onEditAttribute],
    );

    return (
      <div className={styles.ruleClauseRow} data-testid={`rule-clause-${clause.id}`}>
        <Button
          variant="neutral"
          className={styles.attributePill}
          onClick={handleEditAttribute}
          aria-label={attribute ? `Edit attribute ${attribute.pillLabel}` : "Choose attribute"}
          data-testid={`rule-attribute-${clause.id}`}
          disabled={isReadOnly}
          icon="edit"
          iconPosition="right"
          isFullWidth
          contentClassName={styles.attributePillContent}
          alignText="left"
          alignParent="top"
        >
          {/* TODO: update after DS pre-release */}
          <span className={styles.attributePillLabel}>{pillLabel}</span>
        </Button>
        <div
          className={classNames(styles.operatorSelect, {
            [styles.operatorSelectRuntime]: usesRuntimeValues,
          })}
          data-testid={`rule-operator-cell-${clause.id}`}
        >
          <Select
            value={usesRuntimeValues ? RUNTIME_OPERATOR_VALUE : clause.operator}
            onChange={handleOperatorChange}
            aria-label="Operator"
            data-testid={`rule-operator-${clause.id}`}
            disabled={isReadOnly}
            full
          >
            <Select.Section>
              {displayedOperatorOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </div>
        {!usesRuntimeValues && (
          <RuleValueEditor
            clauseId={clause.id}
            attribute={attribute}
            operator={clause.operator}
            value={clause.value}
            showError={showError}
            isReadOnly={isReadOnly}
            arenaId={arenaId}
            onChange={handleValueChange}
          />
        )}
        {!isReadOnly && (
          <Button
            variant="muted"
            childAsIcon={{ type: "trash" }}
            onClick={handleRemove}
            aria-label="Remove rule"
            data-testid={`rule-remove-${clause.id}`}
          />
        )}
      </div>
    );
  },
);
RuleClauseRow.displayName = "RuleClauseRow";

type AddOrRuleButtonProps = {
  groupId: string;
  testId: string;
  onAddOrRule: (groupId: string) => void;
};

// Pulled out so the per-group click handler is a method on a stable component
// instead of an inline arrow inside the parent's render — avoids re-creating
// the closure for every group on every form-value change.
const AddOrRuleButton = memo(({ groupId, testId, onAddOrRule }: AddOrRuleButtonProps) => {
  const handleClick = useCallback(() => onAddOrRule(groupId), [groupId, onAddOrRule]);
  return (
    <Button
      variant="link"
      icon="plus"
      className={styles.addRuleLink}
      onClick={handleClick}
      data-testid={testId}
    >
      &quot;or&quot; rule
    </Button>
  );
});
AddOrRuleButton.displayName = "AddOrRuleButton";

type RulesSectionContentProps = RulesSectionProps & {
  attributeLeaves: ReadonlyArray<AttributeLeaf>;
};

const RulesSectionContent = ({
  title,
  description,
  name,
  isReadOnly = false,
  attributeFilter,
  arenaId,
  allowRuntimeValues = false,
  attributeLeaves,
  variant = "card",
}: RulesSectionContentProps) => {
  const [field, , helpers] = useField<Rules>(name);
  const rules = field.value;
  // `submitCount` is the most reliable signal that the user tried to save —
  // Formik's `meta.touched` for an object field flips on submit, but the
  // structural shape varies by Formik version. We use the count instead so
  // tests can assert "errors render after submit".
  const { submitCount } = useFormState<unknown>();
  const showError = submitCount > 0;

  const [modalState, setModalState] = useState<ModalState>({ mode: "closed" });

  // The catalogue carries non-filterable columns for metadata lookups
  // (clause labels above, level support elsewhere); only filterable ones
  // may be picked as rule attributes.
  const pickableAttributeLeaves = useMemo(
    () => attributeLeaves.filter((leaf) => leaf.filterable),
    [attributeLeaves],
  );

  // Refs let us keep the change/remove/edit handlers below stable across
  // renders. Without this, every keystroke in any clause Select would
  // re-create the callbacks (because they'd close over `rules.groups`),
  // which in turn would invalidate every memoized `RuleClauseRow` and
  // defeat the point of `memo()`. The ref pattern reads the latest groups
  // at call time without making the callback identity depend on it.
  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const modalStateRef = useRef(modalState);
  modalStateRef.current = modalState;

  const setRulesRef = useRef(helpers.setValue);
  setRulesRef.current = helpers.setValue;

  const closeModal = useCallback(() => setModalState({ mode: "closed" }), []);

  const handleConfirmAttribute = useCallback((attributePath: string) => {
    const groups = applyAttributeToRules(
      rulesRef.current,
      modalStateRef.current,
      attributePath,
    );
    void setRulesRef.current({ ...rulesRef.current, groups });
    setModalState({ mode: "closed" });
  }, []);

  const updateClause = useCallback((groupId: string, updatedClause: RuleClause) => {
    const groups = rulesRef.current.groups.map((group) =>
      group.id === groupId
        ? {
            ...group,
            clauses: group.clauses.map((clause) =>
              clause.id === updatedClause.id ? updatedClause : clause,
            ),
          }
        : group,
    );
    void setRulesRef.current({ ...rulesRef.current, groups });
  }, []);

  const removeClause = useCallback((groupId: string, clauseId: string) => {
    const groups = rulesRef.current.groups
      .map((group) =>
        group.id === groupId
          ? { ...group, clauses: group.clauses.filter((clause) => clause.id !== clauseId) }
          : group,
      )
      // Drop now-empty groups so we don't render a bordered card with no
      // clauses inside.
      .filter((group) => group.clauses.length > 0);
    void setRulesRef.current({ ...rulesRef.current, groups });
  }, []);

  const handleEditAttribute = useCallback(
    (groupId: string, clauseId: string) =>
      setModalState({ mode: "editClause", groupId, clauseId }),
    [],
  );

  const handleAddOrRule = useCallback(
    (groupId: string) => setModalState({ mode: "addClause", groupId }),
    [],
  );

  const handleAddRule = useCallback(() => setModalState({ mode: "addGroup" }), []);

  const isEmpty = rules.groups.length === 0;
  // When re-opening the picker in edit mode, look up the currently-selected
  // attribute path so the modal can pre-select it in its tree.
  const initialPath =
    modalState.mode === "editClause"
      ? (rules.groups
          .find((group) => group.id === modalState.groupId)
          ?.clauses.find((clause) => clause.id === modalState.clauseId)?.attribute ?? null)
      : null;

  const body = (
    <>
      <Flex flexDirection="column">
        {rules.groups.map((group, groupIndex) => (
          <Flex flexDirection="column" key={group.id}>
            {groupIndex > 0 && <ConjunctionDivider conjunction={rules.groupsOperator} />}
            <Flex flexDirection="column" className={styles.ruleGroup}>
              {group.clauses.map((clause, clauseIndex) => (
                <Flex flexDirection="column" key={clause.id}>
                  {clauseIndex > 0 && (
                    <ConjunctionDivider conjunction={group.conditionOperator} />
                  )}
                  <RuleClauseRow
                    clause={clause}
                    groupId={group.id}
                    showError={showError}
                    isReadOnly={isReadOnly}
                    arenaId={arenaId}
                    allowRuntimeValues={allowRuntimeValues}
                    attributeLeaves={attributeLeaves}
                    onChange={updateClause}
                    onRemove={removeClause}
                    onEditAttribute={handleEditAttribute}
                  />
                </Flex>
              ))}
              {!isReadOnly && (
                <AddOrRuleButton
                  groupId={group.id}
                  testId={`${name}-add-or-${group.id}`}
                  onAddOrRule={handleAddOrRule}
                />
              )}
            </Flex>
          </Flex>
        ))}
        {!isReadOnly && (
          <Button
            variant="link"
            icon="plus"
            className={styles.addRuleLink}
            onClick={handleAddRule}
            data-testid={`${name}-add-rule`}
          >
            {isEmpty ? "Add rule" : '"and" rule'}
          </Button>
        )}
      </Flex>
      <SelectAttributeModal
        isOpen={modalState.mode !== "closed"}
        onClose={closeModal}
        onConfirm={handleConfirmAttribute}
        initialPath={initialPath}
        attributeFilter={attributeFilter}
        attributeLeaves={pickableAttributeLeaves}
      />
    </>
  );

  if (variant === "inline") {
    return (
      <Flex flexDirection="column" data-field={name} data-testid={`${name}-card`}>
        <Text variant="strong-md">{title}</Text>
        <Text variant="secondary-md" marginBottom="md">
          {description}
        </Text>
        {body}
      </Flex>
    );
  }

  return (
    <Card
      isFullWidth
      title={title}
      data-field={name}
      data-testid={`${name}-card`}
      subTitle={
        <Text variant="secondary-md" marginBottom="md">
          {description}
        </Text>
      }
    >
      {body}
    </Card>
  );
};

const RulesSectionWithLoadedAttributes = (props: RulesSectionProps) => {
  const primaryFieldItems = useAllPrimaryFields(props.arenaId ?? null);
  const attributeLeaves = useMemo(() => {
    return toAttributeLeaves(primaryFieldItems);
  }, [primaryFieldItems]);

  return <RulesSectionContent {...props} attributeLeaves={attributeLeaves} />;
};

export const RulesSection = (props: RulesSectionProps) => {
  if (props.attributeLeaves) {
    return <RulesSectionContent {...props} attributeLeaves={props.attributeLeaves} />;
  }
  return <RulesSectionWithLoadedAttributes {...props} />;
};
